import React from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';

import ProgrammeCardsPanel from './ProgrammeCardsPanel';
import AxisSummaryPanel from './AxisSummaryPanel';
import StatusSummaryPanel from './StatusSummaryPanel';
import ProgrammeReviewGrid from './ProgrammeReviewGrid';

import { ActivityProgrammeRow, AxisSummary, ProgrammeCards, StatusSummary } from './types';
import { fetchAxisSummary, fetchProgrammeCards, fetchProgrammeReview, fetchStatusSummary } from './activityProgrammeApi';
import ActivityProgrammeFilterBar from './ActivityProgrammeFilterBar';
import { ComboRow } from '../core/coremodels';
import { fetchCombo } from '../core/coreapi';

type State = {
  cycle: string;
  costcenter: string;
  axe: string;
  status: string;

  cycles: ComboRow[];
  costcenters: ComboRow[];
  axes: ComboRow[]; 
  statuses: ComboRow[];

  cards: ProgrammeCards | null;
  axesummaries: AxisSummary[];
  statussummaries: StatusSummary[];
  rows: ActivityProgrammeRow[];

  loading: boolean;
  error: string;
};

class ActivityProgrammeReviewWorkbench extends React.Component<any, State> {
  state: State = {
    cycle: '',
    costcenter: '',
    axe: '',
    status: '',

    cycles: [],
    costcenters: [],
    axes: [],
    statuses: [],

    cards: null,
    axesummaries: [],
    statussummaries: [],
    rows: [],

    loading: false,
    error: '',
  };

  async componentDidMount() {
    await this.loadCombos();
    await this.load();
  }

  // setField = (name: 'cycle' | 'costcenter' | 'axe' | 'status') => (e: React.ChangeEvent<HTMLInputElement>) => {
  //   this.setState({ [name]: e.target.value.toUpperCase() } as Pick<State, 'cycle' | 'costcenter' | 'axe' | 'status'>);
  // };

  setFieldValue = (name: any) => (value:any) => {
    this.setState({ [name]: value.toUpperCase() } as Pick<State, any>);
  };

  // Optional immediate-load handler:
  setFieldValueAndLoad = (name: any) => (value: any) => {
    this.setState(
      { [name]: String(value || '').toUpperCase() } as Pick<State, any>,
      this.load
    );
  };

  filters = () => {
    const { cycle, costcenter, axe, status } = this.state;
    return {
      bcc: cycle || null,
      ccc: costcenter || null,
      axc: axe || null,
      stc: status || null,
    };
  };

  loadCombos = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const [cycles, costcenters, axes, statuses] = await Promise.all([
        fetchCombo('budgetcycle'),
        fetchCombo('costcenter'),
        fetchCombo('axis'),
        fetchCombo('activitystatus'),
      ]);

      console.log('cycles',cycles);

      this.setState({ cycles, costcenters, axes, statuses });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  load = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const filters = this.filters();
      const [cards, axesummaries, statussummaries, rows] = await Promise.all([
        fetchProgrammeCards(filters),
        fetchAxisSummary(filters),
        fetchStatusSummary(filters),
        fetchProgrammeReview(filters, 0, 1000),
      ]);
      this.setState({ cards, axesummaries, statussummaries, rows });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  clear = () => {
    this.setState(
      {
        cycle: '',
        costcenter: '',
        axe: '',
        status: '',

        cards: null,
        axesummaries: [],
        statussummaries: [],
        rows: [],

        error: '',
      },
      this.load,
    );
  };

  notifyResponsible = (row: ActivityProgrammeRow) => {
    console.log('Notify responsable:', row);
  };

  notifyIncompleteResponsible = (row: ActivityProgrammeRow) => {
    console.log('Notify incomplete responsable:', row);
  };

  notifyAllIncomplete = () => {
    console.log('Notify all incomplete rows');
  };

  notifyAllResponsible = () => {
    console.log('Notify all responsables');
  };

  render() {
    const { cycle, costcenter, axe, status,  } = this.state;
    const { cycles, costcenters, axes, statuses,  } = this.state;
    const { cards, axesummaries, statussummaries, rows } = this.state;
    const { loading, error } = this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Activity Programme Review</Typography>
            <Typography variant="body2" color="text.secondary">
              Horizontal spreadsheet-style validation view for Programme d'Activités submissions.
            </Typography>
          </Box>
          <Button variant="contained" onClick={this.load} disabled={loading}>Refresh</Button>
        </Stack>

        <ActivityProgrammeFilterBar
          cycle={cycle}
          costcenter={costcenter}
          axe={axe}
          status={status}

          cycles={cycles}
          costcenters={costcenters}
          axes={axes}
          statuses={statuses}

          onCycleChange={this.setFieldValue('cycle')}
          onCostcenterChange={this.setFieldValue('costcenter')}
          onAxeChange={this.setFieldValue('axe')}
          onStatusChange={this.setFieldValue('status')}

          onRefresh={this.load}
          onClear={this.clear}
          loading={loading}
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}><CircularProgress size={22} /><Typography>Loading programme review...</Typography></Stack>}

        <Stack spacing={3}>
          <ProgrammeCardsPanel cards={cards} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 7 }}>
              <AxisSummaryPanel rows={axesummaries} />
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }}>
              <StatusSummaryPanel rows={statussummaries} />
            </Grid>
          </Grid>

          {/* <ProgrammeReviewGrid rows={rows} /> */}
          <ProgrammeReviewGrid
            rows={rows}
            loading={loading}
            onNotifyResponsible={this.notifyResponsible}
            onNotifyIncompleteResponsible={this.notifyIncompleteResponsible}
            onNotifyAllIncomplete={this.notifyAllIncomplete}
            onNotifyAllResponsible={this.notifyAllResponsible}
          />
        </Stack>
      </Box>
    );
  }
}

export default ActivityProgrammeReviewWorkbench;


